import { useEffect, useState } from "react";
import { X, Check, Zap, Crown, Sparkles, Briefcase, Loader2, Gift, CreditCard, Wallet } from "lucide-react";
import { checkoutSubscription, fetchPricing, checkoutPaypalSubscription, paypalStatus } from "../lib/api";
import { getStoredEmail } from "../lib/useNewsezeUser";

export default function PricingModal({ onClose, onNeedSubscribe }) {
  const email = getStoredEmail();
  const [pricing, setPricing] = useState(null);
  const [paypalReady, setPaypalReady] = useState(false);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    fetchPricing().then(setPricing).catch(() => {});
    paypalStatus().then((s) => setPaypalReady(!!s?.enabled)).catch(() => setPaypalReady(false));
  }, []);

  const buy = async (tier, processor = "stripe") => {
    setErr("");
    if (!email) {
      onNeedSubscribe?.();
      return;
    }
    setBusy(`${tier}:${processor}`);
    try {
      const payload = { email, tier, origin_url: window.location.origin };
      const { url } = processor === "paypal"
        ? await checkoutPaypalSubscription(payload)
        : await checkoutSubscription(payload);
      window.location.href = url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not start checkout. Try again.");
      setBusy("");
    }
  };

  const basic = pricing?.products?.basic || pricing?.products?.lite;
  const pro = pricing?.products?.pro;
  const channels = pricing?.products?.channels;
  const elite = pricing?.products?.elite || pricing?.products?.business;

  const Tile = ({ kind, tier, eyebrow, IconCls, price, label, features, highlight, testid }) => {
    const isPro = kind === "pro";
    const isPlus = kind === "plus";
    const isChannels = kind === "channels";
    const isElite = kind === "elite";
    const cardCls = isPlus
      ? "rounded-xl border-2 border-[var(--gold)] p-5 bg-gradient-to-br from-[#fff7d6] via-[#fde68a] to-[#f59e0b] text-[#1d1303] relative shadow-xl"
      : isPro
      ? "rounded-xl border-2 border-[var(--gold)] p-5 bg-gradient-to-br from-[#0e2d63] to-[#1e40af] text-white relative"
      : isChannels
        ? "rounded-xl border-2 border-[#7c3aed] p-5 bg-gradient-to-br from-[#4c1d95] to-[#7c3aed] text-white relative"
        : isElite
          ? "rounded-xl border-2 border-[var(--gold)] p-5 bg-gradient-to-br from-[#1d1303] via-[#3f2f0a] to-[#0a1f3d] text-white relative"
          : kind === "free"
            ? "rounded-xl border border-[var(--line)] p-5 bg-white relative"
            : "rounded-xl border border-[var(--line)] p-5 bg-[var(--sky)] relative";
    const eyebrowCls = isPlus ? "text-[#1d1303]" : (isPro || isChannels || isElite ? "text-[var(--gold)]" : "text-[var(--blue)]");
    const subCls = isPlus ? "opacity-90" : (isPro || isChannels || isElite ? "opacity-85" : "text-[var(--muted)]");
    const checkCls = isPlus ? "text-[#1d1303]" : (isPro || isChannels || isElite ? "text-[var(--gold)]" : "text-[var(--blue)]");
    const btnCls = isPlus
      ? "btn w-full justify-center mt-5 bg-[#1d1303] text-[var(--gold)] hover:bg-black"
      : (isPro || isChannels || isElite ? "btn gold w-full justify-center mt-5" : "btn primary w-full justify-center mt-5");
    return (
      <div className={cardCls} data-testid={testid}>
        {highlight && (
          <span className="absolute -top-3 left-5 bg-[var(--gold)] text-[#1d1303] text-[10px] uppercase tracking-[0.18em] font-black px-2 py-1 rounded whitespace-nowrap">{highlight}</span>
        )}
        <div className={`flex items-center gap-2 ${eyebrowCls}`}><IconCls size={16} /> <span className="text-[11px] uppercase tracking-[0.14em] font-black">{eyebrow}</span></div>
        <div className="mt-3 flex items-baseline gap-1">
          <span className="text-[36px] font-black tracking-[-0.03em]">{price === 0 ? "Free" : `$${price}`}</span>
          {price > 0 && <span className="text-[13px] opacity-80">/mo</span>}
        </div>
        <p className={`text-[12.5px] mt-1 leading-snug ${subCls}`}>{label}</p>
        <ul className="mt-4 space-y-2 text-[13.5px]">
          {features.map((x) => (
            <li key={x} className="flex items-start gap-2"><Check size={13} className={`${checkCls} mt-0.5 shrink-0`} /><span>{x}</span></li>
          ))}
        </ul>
        {tier === "free" ? (
          <div className="text-[11.5px] text-center text-[var(--muted)] mt-5 italic" data-testid={`${testid}-free-note`}>Subscribe with email — no card required.</div>
        ) : (
          <>
            <button onClick={() => buy(tier, "stripe")} disabled={busy === `${tier}:stripe`} className={btnCls} data-testid={`${testid}-buy`}>
              {(busy === `${tier}:stripe`) ? <Loader2 size={14} className="animate-spin" /> : (
                isPlus ? <><Gift size={13} /> Get Newseze Plus</>
                : isPro ? <><Zap size={13} /> Start Pro</>
                : isChannels ? <><Sparkles size={13} /> Build Channels</>
                : isElite ? <><Crown size={13} /> Go Elite</>
                : <>Start {eyebrow}</>
              )}
            </button>
            {/* Stripe Checkout accepts: Card · Apple Pay · Google Pay · Link · Cash App */}
            <div className={`text-[10px] uppercase tracking-[0.14em] font-bold mt-2 text-center flex items-center justify-center gap-1 ${isPlus || isPro || isChannels || isElite ? "opacity-80" : "text-[var(--muted)]"}`} data-testid={`${testid}-methods`}>
              <CreditCard size={10} /> Card · Apple Pay · Google Pay · Cash App
            </div>
            {paypalReady && (
              <button onClick={() => buy(tier, "paypal")} disabled={busy === `${tier}:paypal`}
                      className="btn w-full justify-center mt-2 bg-[#ffc439] hover:bg-[#ffb800] text-[#0a1f3d] font-black"
                      data-testid={`${testid}-paypal`}>
                {(busy === `${tier}:paypal`) ? <Loader2 size={14} className="animate-spin" /> : (
                  <><Wallet size={13} /> Pay with PayPal</>
                )}
              </button>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/55 flex items-center justify-center p-4" onClick={onClose} data-testid="pricing-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-[1200px] max-h-[92vh] overflow-auto border border-[var(--line)]">
        <div className="flex items-center justify-between p-5 border-b border-[var(--line)] sticky top-0 bg-white z-10">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-black text-[var(--gold)]">Upgrade Newseze</div>
            <h2 className="text-[22px] font-black tracking-[-0.02em]">Pick your plan. Cancel anytime.</h2>
          </div>
          <button onClick={onClose} aria-label="Close" className="p-2 -mr-2"><X /></button>
        </div>

        <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          <Tile
            kind="free" tier="free"
            eyebrow="Newseze Free" IconCls={Gift}
            price={0}
            label="Calm AI-curated news, sourced from 36+ verified newsrooms. Earn credits."
            features={[
              "Full headline feed (hourly refresh)",
              "Earn credits — gift cards, member benefits, charity",
              "Local news (Near You)",
              "100+ Member Benefits Hub",
              "No drama. No conspiracies.",
            ]}
            testid="pricing-free"
          />
          <Tile
            kind="basic" tier="basic"
            eyebrow="Reader" IconCls={Sparkles}
            price={basic?.amount ?? "6.99"}
            label={basic?.label || "Personalized news + Search & Ask Newseze"}
            features={[
              "Everything in Free",
              "Personalize your news (Buildeze)",
              "Unlimited Search & Ask Newseze",
              "Ad-light reading",
              "2× credit earning on every story",
            ]}
            testid="pricing-basic"
          />
          <Tile
            kind="plus" tier="plus"
            eyebrow="Plus · Best Value" IconCls={Gift}
            price={pricing?.products?.plus?.amount ?? "9.99"}
            label={pricing?.products?.plus?.label || "Pro features + $20 in monthly reading credits"}
            features={[
              "Everything in Pro (all subscription channels · ad-free)",
              "Monthly grant: 20,000 credits ($20 redeemable value)",
              "Redeem credits for gift cards, charity, or Sellpages boosts",
              "$9.99/mo · ~$30 in actual value · cancel anytime",
              "Newseze's best deal — by far",
            ]}
            highlight="Pay $9.99 → Get $30"
            testid="pricing-plus"
          />
          <Tile
            kind="pro" tier="pro"
            eyebrow="Pro" IconCls={Crown}
            price={pro?.amount ?? "10.99"}
            label={pro?.label || "All subscription channels unlocked"}
            features={[
              "Everything in Reader",
              "All subscription channels (Newsstand magazines · premium podcasts)",
              "Ad-free reading",
              "Unlimited Boost a Subject",
              "Premium briefs (350+ words)",
              "Member-Benefit Unlock at 2,500 credits",
              "Three-avatar audio briefs",
            ]}
            testid="pricing-pro"
          />
          <Tile
            kind="channels" tier="channels"
            eyebrow="Channel Builder" IconCls={Briefcase}
            price={channels?.amount ?? "19.99"}
            label={channels?.label || "Build your own custom interest channels"}
            features={[
              "Everything in Pro",
              "Build your own custom channels",
              "High-value interests: Classic Cars · Watches · Aviation · Real Estate · Sports Cards · Guitars · Precious Metals",
              "Algorithm-curated feeds per channel (auctions, market values, news)",
              "Share your channels with friends",
              "Channel-specific notifications",
            ]}
            testid="pricing-channels"
          />
          <Tile
            kind="elite" tier="elite"
            eyebrow="Elite — Custom Channel" IconCls={Crown}
            price={elite?.amount ?? "49.99"}
            label={elite?.label || "Build a fully branded high-value channel"}
            features={[
              "Everything in Channel Builder",
              "Build a fully branded channel (e.g., your own car channel)",
              "Custom RSS curation — bring your own sources",
              "Your channel listed in Newseze Networks",
              "Pro Analysis ($1 each — free for you)",
              "Priority support · API access",
              "Sponsor-revenue share on your channel",
            ]}
            highlight="Build your own"
            testid="pricing-elite"
          />
        </div>

        {err && <div className="px-5 pb-4 text-[13px] text-[var(--red,#b91c1c)]" data-testid="pricing-error">{err}</div>}
        <div className="px-5 pb-5 text-[11px] text-[var(--muted)] text-center">
          Cancel anytime in your wallet. Powered by Stripe + PayPal. Card · Apple Pay · Google Pay · Cash App · PayPal — all secure, all auto-renew, no hidden fees.
          Rewards are virtual + gift cards (US/UK/CA/AU/IE). No direct cash payouts.
        </div>
      </div>
    </div>
  );
}
